import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Users, BookOpen, ArrowRight } from "lucide-react";

const Impact = () => {
  const youthPoints = [
    "Young people experience Nepali culture in modern, engaging, and meaningful ways.",
    "Families create memories together across generational boundaries.",
    "Second-generation Nepalis reconnect with language and identity.",
    "Young creatives see pride, possibility, and purpose reflected back at them.",
  ];

  const givingAreas = [
    {
      icon: Heart,
      title: "Nonprofit Support",
      description:
        "We support nonprofit organizations and initiatives that directly serve Nepali communities in the United States.",
    },
    {
      icon: BookOpen,
      title: "Cultural Preservation",
      description:
        "A portion of all earnings goes toward preserving Nepali culture, heritage, language, music, and traditions.",
    },
    {
      icon: Users,
      title: "Youth Education",
      description:
        "We invest in youth education and empowerment programs that help the next generation stay connected to their roots.",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="impact-page"
    >
      {/* Hero */}
      <section className="impact-hero">
        <div className="impact-hero-overlay" />
        <motion.div
          className="impact-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Culture Thrives When Communities Are Invested In
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Entertainment should do more than entertain. Every 3E event is
            designed to create lasting community impact.
          </motion.p>
        </motion.div>
      </section>

      {/* Youth Section */}
      <section className="youth-section">
        <div className="impact-container">
          <div className="youth-layout">
            <motion.div
              className="youth-text"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>
                Inspiring the Next Generation of Nepali Americans
              </h2>
              <p>
                We believe culture survives when the next generation feels seen,
                included, and inspired. That is why youth engagement is central
                to our mission — not an add-on.
              </p>
              <ul className="youth-points">
                {youthPoints.map((point, i) => (
                  <li key={i}>
                    <span className="youth-point-dot" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="youth-visual"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="youth-image-wrap">
                <img src="/images/hm-ab.jpg" alt="Youth at 3E Events" loading="lazy" decoding="async" />
                <div className="youth-image-overlay" />
              </div>
              <div className="youth-stat-card">
                <span className="youth-stat-number">2021</span>
                <span className="youth-stat-label">Officially Established</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Giving Section */}
      <section className="giving-section">
        <div className="impact-container">
          <div className="giving-header">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              Every Ticket Sold Gives Back
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
            >
              Through our 20% Giving Model, 3E Entertainment commits a portion
              of all earnings to nonprofit organizations and initiatives. Every
              ticket sold, every partnership formed, and every event produced
              contributes to a deeper mission of connection, healing, and
              long-term community progress.
            </motion.p>
          </div>

          <div className="giving-grid">
            {givingAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <motion.div
                  key={area.title}
                  className="giving-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.12 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6, transition: { duration: 0.3 } }}
                >
                  <div className="giving-card-icon">
                    <Icon size={24} />
                  </div>
                  <h4>{area.title}</h4>
                  <p>{area.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="impact-cta-section">
        <div className="impact-cta-overlay" />
        <motion.div
          className="impact-cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2>Be Part of Something Bigger</h2>
          <p>
            When you attend a 3E event, you are not just celebrating culture.
            You are investing in community, education, and a legacy that extends
            far beyond the stage.
          </p>
          <div className="impact-cta-btns">
            <Link to="/all-events" className="impact-btn-primary">
              Explore Events <ArrowRight size={16} />
            </Link>
            <Link to="/partners" className="impact-btn-secondary">
              Partner With Us
            </Link>
          </div>
        </motion.div>
      </section>

      <style>{`
        .impact-page { overflow-x: hidden; }

        .impact-hero {
          position: relative;
          min-height: 70vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/about-hero.jpg") center/cover no-repeat;
        }

        .impact-hero-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.82);
        }

        .impact-hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 0 2rem;
        }


        .impact-hero-content h1 {
          font-size: clamp(2.5rem, 5.5vw, 3.8rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
          line-height: 1.1;
        }

        .impact-hero-content p {
          font-size: 1.15rem;
          color: rgba(255, 255, 255, 0.85);
          line-height: 1.7;
          max-width: 580px;
          margin: 0 auto;
        }

        .impact-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }



        /* Youth Section */
        .youth-section {
          padding: 7rem 0;
          background: #fff;
        }

        .youth-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5rem;
          align-items: center;
        }

        .youth-text h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1.25rem;
          line-height: 1.1;
        }

        .youth-text > p {
          font-size: 1.05rem;
          color: #555;
          line-height: 1.8;
          margin: 0 0 1.75rem;
        }

        .youth-points {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }

        .youth-points li {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          font-size: 0.98rem;
          color: #444;
          line-height: 1.6;
        }

        .youth-point-dot {
          width: 8px;
          height: 8px;
          background: #C72631;
          border-radius: 50%;
          flex-shrink: 0;
          margin-top: 6px;
        }

        .youth-visual {
          position: relative;
        }

        .youth-image-wrap {
          border-radius: 16px;
          overflow: hidden;
          position: relative;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
        }

        .youth-image-wrap img {
          width: 100%;
          height: 460px;
          object-fit: cover;
          display: block;
        }

        .youth-image-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.1);
        }

        .youth-stat-card {
          position: absolute;
          bottom: -20px;
          left: -20px;
          background: #C72631;
          color: #fff;
          padding: 1.5rem 2rem;
          border-radius: 12px;
          z-index: 3;
          display: flex;
          flex-direction: column;
          align-items: center;
          box-shadow: 0 10px 30px rgba(199, 38, 49, 0.3);
        }

        .youth-stat-number {
          font-size: 2rem;
          font-weight: 900;
          line-height: 1;
          color: #fff;
        }

        .youth-stat-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #ffffff;
          margin-top: 0.25rem;
          text-align: center;
        }

        /* Giving Section */
        .giving-section {
          padding: 7rem 0;
          background: #0a0a0a;
        }

        .giving-header {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 4rem;
        }

        .giving-header h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #fff;
          margin: 0 0 1.25rem;
          line-height: 1.1;
        }

        .giving-header p {
          font-size: 1.05rem;
          color: rgba(255, 255, 255, 0.65);
          line-height: 1.8;
          margin: 0;
        }

        .giving-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }

        .giving-card {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 2.5rem 2rem;
          transition: background 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.3s ease;
          cursor: default;
        }

        .giving-card:hover {
          background: rgba(255, 255, 255, 0.07);
        }

        .giving-card-icon {
          width: 52px;
          height: 52px;
          background: rgba(199, 38, 49, 0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #C72631;
          margin-bottom: 1.5rem;
        }

        .giving-card h4 {
          font-size: 1.15rem;
          font-weight: 700;
          color: #fff;
          margin: 0 0 0.75rem;
        }

        .giving-card p {
          font-size: 0.92rem;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.75;
          margin: 0;
        }

        /* CTA Section */
        .impact-cta-section {
          position: relative;
          padding: 7rem 2rem;
          background: url("/images/about-2.jpg") center/cover no-repeat;
          text-align: center;
        }

        .impact-cta-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .impact-cta-content {
          position: relative;
          z-index: 2;
          max-width: 680px;
          margin: 0 auto;
        }

        .impact-cta-content h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #fff;
          margin: 0 0 1.25rem;
        }

        .impact-cta-content p {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.75;
          margin: 0 0 2.5rem;
        }

        .impact-cta-btns {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .impact-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #C72631;
          color: #fff;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: background 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.3s ease;
        }

        .impact-btn-primary:hover {
          background: #a01f28;
          transform: translateY(-3px);
        }

        .impact-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: transparent;
          color: #fff;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1px;
          border: 2px solid rgba(255, 255, 255, 0.4);
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .impact-btn-secondary:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: #fff;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .youth-layout {
            grid-template-columns: 1fr;
            gap: 3rem;
          }

          .giving-grid {
            grid-template-columns: 1fr;
            max-width: 460px;
            margin: 0 auto;
          }
        }

        @media (max-width: 768px) {
          .youth-section,
          .giving-section {
            padding: 4.5rem 0;
          }

          .impact-container {
            padding: 0 1.5rem;
          }

          .youth-image-wrap img {
            height: 300px;
          }

          .youth-stat-card {
            position: relative;
            bottom: auto;
            left: auto;
            display: inline-flex;
            flex-direction: row;
            gap: 0.75rem;
            align-items: center;
            margin-top: 1.5rem;
          }

          .impact-cta-btns {
            flex-direction: column;
            align-items: center;
          }

          .impact-btn-primary,
          .impact-btn-secondary {
            width: 100%;
            max-width: 280px;
            justify-content: center;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Impact;
